// Ranks one job's vendor costs for the "Cost breakdown by vendor" card. All math is in whole cents.
// Shares are in tenths of a percent (423 = 42.3%) and use the largest-remainder method, so the shares of
// all vendors add up to exactly 100.0% and the "All other vendors" share is the exact sum of what it holds.
import { toWholeCents } from "./money";
import { DEFAULT_BUSINESS_TYPE } from "./businessPreset";

export const TOP_VENDORS = 6;

// costByVendor: { "Vendor name": dollars }. Returns:
//   totalCents, rows (every vendor, largest first, each { name, cents, tenths }),
//   top (the rows shown by default), other ({ count, cents, tenths } or null).
// If only one vendor would be rolled up, it is shown by name instead.
export function vendorBreakdown(costByVendor, topCount = TOP_VENDORS) {
  const entries = Object.entries(costByVendor || {})
    .map(([name, dollars]) => ({ name, cents: toWholeCents(Number(dollars) || 0) }))
    .filter(r => r.cents > 0)
    .sort((a, b) => b.cents - a.cents || a.name.localeCompare(b.name));
  const totalCents = entries.reduce((s, r) => s + r.cents, 0);
  if (totalCents === 0) return { totalCents: 0, rows: [], top: [], other: null };

  // Largest remainder: floor each share in tenths, then hand the leftover tenths to the biggest remainders.
  const parts = entries.map((r, i) => {
    const exact = r.cents * 1000;
    return { i, tenths: Math.floor(exact / totalCents), rem: exact % totalCents };
  });
  let left = 1000 - parts.reduce((s, p) => s + p.tenths, 0);
  [...parts].sort((a, b) => b.rem - a.rem || a.i - b.i).forEach(p => { if (left > 0) { p.tenths += 1; left -= 1; } });
  const rows = entries.map((r, i) => ({ ...r, tenths: parts[i].tenths }));

  const rest = rows.slice(topCount);
  if (rest.length < 2) return { totalCents, rows, top: rows, other: null };
  return {
    totalCents,
    rows,
    top: rows.slice(0, topCount),
    other: { count: rest.length, cents: rest.reduce((s, r) => s + r.cents, 0), tenths: rest.reduce((s, r) => s + r.tenths, 0) },
  };
}

// 423 -> "42.3%", 400 -> "40%", 4 -> "0.4%".
export function formatShare(tenths) {
  const whole = Math.floor(tenths / 10);
  const frac = tenths % 10;
  return frac === 0 ? `${whole}%` : `${whole}.${frac}%`;
}

// The rows of the "Revenue and costs" card, in dollars as stored on the job. Bar widths are a share of
// the larger of revenue and total costs, in whole percent, so every bar is on one scale.
// businessType (src/businessPreset.js) decides the cost rows; without one it is Trades, the original four rows:
//   trades: Revenue, Materials, Labor, Profit   team: Revenue, Contractors, Expenses, Profit   solo: Revenue, Costs, Profit
// The profit figure is the job's own, so it is the same under every business type.
export function revenueCostRows(job, businessType) {
  const bt = businessType || DEFAULT_BUSINESS_TYPE;
  const rev = toWholeCents(job.revenue || 0);
  const mat = toWholeCents(job.materialCost || 0);
  const lab = toWholeCents(job.laborCost || 0);
  const profit = toWholeCents(job.profit || 0);
  const scale = Math.max(rev, mat + lab, Math.abs(profit), 1);
  const w = c => Math.min(100, Math.round((Math.abs(c) * 100) / scale));
  const revenue = { key: "revenue", label: "Revenue", cents: rev, width: w(rev) };
  const profitRow = { key: "profit", label: "Profit", cents: profit, width: w(profit), loss: profit < 0 };
  const labor = (label) => ({ key: "labor", label, cents: lab, width: w(lab), none: lab === 0 && bt.missingLaborCounts });
  if (bt.key === "solo") return [revenue, { key: "costs", label: "Costs", cents: mat + lab, width: w(mat + lab) }, profitRow];
  if (bt.key === "team") return [revenue, labor("Contractors"), { key: "materials", label: "Expenses", cents: mat, width: w(mat) }, profitRow];
  return [revenue, { key: "materials", label: "Materials", cents: mat, width: w(mat) }, labor("Labor"), profitRow];
}
